import { api } from "@/api"

import type { MaybeRef } from "@vueuse/core"
import { useQuery } from "@tanstack/vue-query"
import type { OrderOpenStatus } from "../sharedTypes/orders"
import type {
  PaymentDetailCard,
  PaymentDetailCash,
  PaymentDetailSplit,
} from "../sharedTypes/invoices"

type OrderFilter = {
  deviceId?: number
  cashierId?: string
  from?: string
  until?: string
  search?: string
}

export type OrderQuery = OrderFilter & Pagination

export type ListOrdersResponseItemInvoice = {
  id: string
  amountGross: number
  currency: string
  createdAt: Date
  isPaid: boolean
  paidAt: Date | null
  paidBy:
    | PaymentDetailCard
    | PaymentDetailCash
    | PaymentDetailSplit
    | null
}

export type ListOrdersResponseItem = {
  id: string
  note: string | null
  numberOfItems: number
  createdAt: Date
  updatedAt: Date
  totalCost: number
  cashierId: string
  invoice: ListOrdersResponseItemInvoice | null
  orderNumber: string | null
  openStatus: OrderOpenStatus
  table: {
    id: string
    name: string
  } | null
  isCollection: boolean
  collectionAt: Date | null
  customerName: string
  orderItems: Array<{
    id: string
    product: {
      id: string
      hasVariablePrice: boolean
      name: string
      color: string | null
    } | null
    price: number
    note: string | null
  }>
}

const parseOrderListEntry = (
  order: RecursivelyConvertDatesToStrings<ListOrdersResponseItem>
): ListOrdersResponseItem => ({
  ...order,
  invoice: order.invoice && {
    ...order.invoice,
    createdAt: DateFns.parseISO(order.invoice.createdAt),
    paidAt: order.invoice.paidAt
      ? DateFns.parseISO(order.invoice.paidAt)
      : null,
  },
  createdAt: DateFns.parseISO(order.createdAt),
  updatedAt: DateFns.parseISO(order.updatedAt),
  collectionAt: order.collectionAt
    ? DateFns.parseISO(order.collectionAt)
    : null,
})

export const listOrders = async (
  query?: OrderQuery
): Promise<Paginate<Array<ListOrdersResponseItem>>> => {
  const {
    body: { items, ...body },
  } = await api.get<
    Paginate<
      RecursivelyConvertDatesToStrings<
        Array<ListOrdersResponseItem>
      >
    >
  >("/orders/", {
    query,
  })
  return {
    items: items.map((order) => parseOrderListEntry(order)),
    ...body,
  }
}

export const useOrders = (query?: MaybeRef<OrderQuery>) =>
  useQuery(
    [BASE_ORDERS_QUERY_KEY, query],
    ({ queryKey: [_name, query_] }) =>
      listOrders(query_ as OrderQuery | undefined),
    { refetchInterval: 60 * 1000, staleTime: 0 }
  )
